import React, { useEffect, useState, useCallback } from "react";
import { FaWallet, FaGlobeAmericas } from "react-icons/fa";
import RegistroEntrada from "../components/finanzas/RegistroEntrada";
import ReporteServicio from "../components/finanzas/ReporteServicio";

export default function Finanzas() {

  const [servicios, setServicios] = useState([]);
  const [servicioSeleccionado, setServicioSeleccionado] = useState("");

  const [reporte, setReporte] = useState([]);

  const [totales, setTotales] = useState({
    diezmo: 0,
    misiones: 0,
    protemplo: 0,
    ofrenda: 0,
    biblia: 0,
    especial: 0,
    total: 0
  });

  const tesoreriaGeneral =
    Number(totales.diezmo || 0) +
    Number(totales.protemplo || 0) +
    Number(totales.ofrenda || 0);

  const tesoreriaMisiones =
    Number(totales.misiones || 0) +
    Number(totales.biblia || 0) +
    Number(totales.especial || 0);

  const formatoMoneda = (numero) =>
    Number(numero || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ============================
  // CARGAR SERVICIOS
  // ============================

  const cargarServicios = useCallback(async () => {
    try {
      const res = await fetch("/api/finanzas/servicios", {
        credentials: "include",
      });
      const data = await res.json();
      setServicios(data);
    } catch (error) {
      console.error("Error cargando servicios", error);
    }
  }, []);

  useEffect(() => {
    cargarServicios();
  }, [cargarServicios]);

  // ============================
  // CARGAR REPORTE
  // ============================

  const cargarReporte = useCallback(async () => {
    if (!servicioSeleccionado) return;
    try {
      const res = await fetch(
        `/api/finanzas/reporte/${servicioSeleccionado}`,
        { credentials: "include" }
      );
      const data = await res.json();
      setReporte(data.registros);
      setTotales(data.totales);
    } catch (error) {
      console.error("Error cargando reporte", error);
    }
  }, [servicioSeleccionado]);

  useEffect(() => {
    if (servicioSeleccionado) cargarReporte();
  }, [servicioSeleccionado, cargarReporte]);

  const metricas = [
    { label: "Diezmos", valor: totales.diezmo },
    { label: "Misiones", valor: totales.misiones },
    { label: "Pro templo", valor: totales.protemplo },
    { label: "Ofrenda", valor: totales.ofrenda },
    { label: "Biblia", valor: totales.biblia },
    { label: "Especial", valor: totales.especial },
    { label: "Total", valor: totales.total, destacado: true },
  ];

  // ============================
  // UI
  // ============================

  return (
    <div className="p-6 bg-slate-50">

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Entradas Financieras</h1>
        <p className="text-sm text-slate-500 mt-0.5">Registra y consulta las entradas de cada servicio.</p>
      </div>

      {/* SELECTOR + TESORERÍAS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <label className="block text-xs font-medium text-slate-700 mb-1">Seleccionar servicio</label>
          <select
            value={servicioSeleccionado}
            onChange={(e) => setServicioSeleccionado(e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          >
            <option value="">Seleccionar servicio</option>
            {servicios.map((s) => (
              <option key={s.id_servicio} value={s.id_servicio}>
                {s.nombre} - {new Date(s.fecha).toLocaleDateString("es-MX", { timeZone: "UTC" })}
              </option>
            ))}
          </select>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex items-center gap-3">
          <span className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
            <FaWallet />
          </span>
          <div className="min-w-0">
            <p className="text-xs text-slate-500">Tesorería General</p>
            <p className="text-xl font-bold text-slate-800 truncate">${formatoMoneda(tesoreriaGeneral)}</p>
            <p className="text-[11px] text-slate-400">Diezmo + Pro templo + Ofrenda</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex items-center gap-3">
          <span className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
            <FaGlobeAmericas />
          </span>
          <div className="min-w-0">
            <p className="text-xs text-slate-500">Tesorería Misiones</p>
            <p className="text-xl font-bold text-slate-800 truncate">${formatoMoneda(tesoreriaMisiones)}</p>
            <p className="text-[11px] text-slate-400">Misiones + Biblia + Especial</p>
          </div>
        </div>
      </div>

      {/* DESGLOSE POR CONCEPTO */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6 flex flex-wrap gap-x-8 gap-y-3">
        {metricas.map((m) => (
          <div key={m.label} className="min-w-[88px]">
            <p className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">{m.label}</p>
            <p className={`text-base font-semibold mt-0.5 ${m.destacado ? "text-indigo-600" : "text-slate-800"}`}>
              ${formatoMoneda(m.valor)}
            </p>
          </div>
        ))}
      </div>

      {/* GRID PRINCIPAL */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-4">
          <RegistroEntrada
            servicio={servicioSeleccionado}
            onGuardado={cargarReporte}
          />
        </div>

        <div className="lg:col-span-8">
          <ReporteServicio
            reporte={reporte}
            onActualizado={cargarReporte}
          />
        </div>
      </div>

    </div>
  );
}
